/**
 * MfaChallengePanel — TOTP / backup-code verification step.
 *
 * SCRUM-57 follow-up to Codex review #2. After `/auth/login` returns
 * `mfa_required`, this panel is shown. The user enters a 6-digit TOTP
 * code (or a backup code) and the panel calls
 * `POST /auth/login/mfa` with the request body shape from
 * `MfaChallengeBodySchema` in `@artemis/validators/auth.mfa`:
 *
 *   { challengeToken: string, code: string }
 *
 * Note the field name remap: the login response delivers the credential
 * as `mfaToken`, the challenge endpoint expects it as `challengeToken`.
 *
 * On success the backend sets the session cookie and returns the
 * `authenticated` discriminator branch — we then call `onSuccess()`
 * which the caller wires to a redirect.
 *
 * Error handling per ADR 0018 D5/D6 + auth.controller.ts L416-454:
 *   - 400 mfa_code_invalid           → "Code didn't match. Try again." (stay)
 *   - 401 token invalid/expired/intent_mismatch / mfa_challenge_exhausted
 *                                    → "Verification expired" + cancel CTA
 *   - 429 mfa_rate_limited           → "Too many attempts" + Retry-After
 *   - other / network                → generic
 *
 * Tokens-only colors via the same semantic CSS variables LoginPage uses;
 * ≥ 44 px touch targets; aria-live status region; numeric inputmode +
 * autocomplete="one-time-code" for SMS-otp keyboard assist (which the
 * native authenticator-app paste flows also benefit from).
 */

import * as React from 'react';
import { Button, TextField } from '@sparta/ui-kit';
import { apiClient } from '../../shared/api-client.js';

interface Props {
  mfaToken: string;
  onSuccess: () => void;
  onCancel: () => void;
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string }
  | { kind: 'expired' };

const TOTP_CODE_PATTERN = /^[0-9]{6,8}$/;
const BACKUP_CODE_MIN = 8;
const BACKUP_CODE_MAX = 32;

export function MfaChallengePanel({
  mfaToken,
  onSuccess,
  onCancel,
}: Props): React.ReactElement {
  const [code, setCode] = React.useState('');
  const [useBackupCode, setUseBackupCode] = React.useState(false);
  const [submitState, setSubmitState] = React.useState<SubmitState>({
    kind: 'idle',
  });

  const submitting = submitState.kind === 'submitting';

  const onSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();

      // Local format validation only — backend remains the source of
      // truth. Surface a friendly hint for the obviously-wrong shape.
      const trimmed = code.trim();
      if (useBackupCode) {
        if (
          trimmed.length < BACKUP_CODE_MIN ||
          trimmed.length > BACKUP_CODE_MAX
        ) {
          setSubmitState({
            kind: 'error',
            message: 'Backup code looks too short or too long.',
          });
          return;
        }
      } else if (!TOTP_CODE_PATTERN.test(trimmed)) {
        setSubmitState({
          kind: 'error',
          message: 'Enter the 6-digit code from your authenticator app.',
        });
        return;
      }

      setSubmitState({ kind: 'submitting' });
      try {
        const { response } = await apiClient.POST('/auth/login/mfa', {
          body: {
            challengeToken: mfaToken,
            code: trimmed,
          },
        });
        if (response.ok) {
          // Session cookie is set by the response. Caller decides where
          // to send the user (typically window.location.assign('/')).
          onSuccess();
          return;
        }
        if (response.status === 400) {
          setSubmitState({
            kind: 'error',
            message: useBackupCode
              ? 'Backup code didn\'t match. Try a different one.'
              : 'Code didn\'t match. Try again.',
          });
          return;
        }
        if (response.status === 401 || response.status === 410) {
          setSubmitState({ kind: 'expired' });
          return;
        }
        if (response.status === 429) {
          setSubmitState({
            kind: 'error',
            message: 'Too many attempts. Please wait a moment and try again.',
          });
          return;
        }
        if (response.status >= 500) {
          setSubmitState({
            kind: 'error',
            message:
              'Verification is temporarily unavailable. Try again shortly.',
          });
          return;
        }
        setSubmitState({
          kind: 'error',
          message: 'Something went wrong. Try again.',
        });
      } catch {
        setSubmitState({
          kind: 'error',
          message: 'Network error. Check your connection and try again.',
        });
      }
    },
    [code, mfaToken, onSuccess, useBackupCode],
  );

  const codeErrorId = 'mfa-code-error';

  // Expired-token branch is its own state because the only sensible action
  // is to restart the login flow — there's no point letting the user retry
  // a code under an expired challenge token.
  if (submitState.kind === 'expired') {
    return (
      <section
        role="status"
        aria-live="polite"
        aria-labelledby="mfa-expired-heading"
        data-testid="mfa-challenge-expired"
        className="rounded-2xl p-5"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <h2
          id="mfa-expired-heading"
          className="text-base font-bold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Verification expired
        </h2>
        <p
          className="mt-2 text-sm"
          style={{ color: 'var(--fg-secondary)' }}
        >
          Your verification window expired before the code was accepted.
          Sign in again to start a fresh challenge.
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="mt-4 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{
            color: 'var(--fg-brand)',
            background: 'transparent',
          }}
        >
          ← Back to sign in
        </button>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="mfa-challenge-heading"
      data-testid="mfa-challenge-panel"
    >
      <h2
        id="mfa-challenge-heading"
        className="text-3xl font-bold"
        style={{ letterSpacing: '-0.6px', color: 'var(--fg-primary)' }}
      >
        Verify your sign-in
      </h2>
      <p
        className="mb-6 mt-1 text-sm"
        style={{ color: 'var(--fg-tertiary)' }}
      >
        {useBackupCode ? (
          <>Enter one of the backup codes you saved when you set up MFA.</>
        ) : (
          <>
            Enter the 6-digit code from your authenticator app to complete
            sign-in.
          </>
        )}
      </p>

      <form onSubmit={onSubmit} noValidate aria-busy={submitting}>
        {/* SCRUM-100: code field composes the kit TextField (size="md",
            monospace, OTP autocomplete). External submit-error region
            below preserved per SCRUM-100 scope (out-of-scope item). */}
        <TextField
          label={useBackupCode ? 'Backup code' : 'Verification code'}
          type="text"
          // `autocomplete="one-time-code"` triggers the OS-level OTP
          // autofill on iOS / Android when the code is delivered via
          // SMS or pasted; harmless for desktop authenticator-app
          // copy-paste and still the right hint per WCAG 2.1
          // section 5.5.2 (Autocomplete is the preferred field hint).
          autoComplete="one-time-code"
          inputMode={useBackupCode ? 'text' : 'numeric'}
          spellCheck={false}
          autoCapitalize="off"
          placeholder={useBackupCode ? 'xxxx-xxxx-xxxx' : '123 456'}
          disabled={submitting}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          monospace
          aria-invalid={submitState.kind === 'error'}
          aria-describedby={
            submitState.kind === 'error' ? codeErrorId : undefined
          }
        />

        <div role="alert" aria-live="polite" className="mb-3 min-h-5">
          {submitState.kind === 'error' ? (
            <p
              id={codeErrorId}
              className="text-sm"
              style={{ color: '#c0392b' }}
            >
              {submitState.message}
            </p>
          ) : null}
        </div>

        {/* SCRUM-101: composes the kit Button (primary, lg, full).
            Inline-style block (background/color + boxShadow) replaced
            by --bg-brand-driven kit styling. */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          full
          disabled={submitting}
          aria-busy={submitting}
          aria-disabled={submitting}
          aria-label={submitting ? 'Verifying' : 'Verify and continue'}
        >
          {submitting ? 'Verifying…' : 'Verify and continue'}
        </Button>

        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              setUseBackupCode((v) => !v);
              setCode('');
              setSubmitState({ kind: 'idle' });
            }}
            className="inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
            style={{
              color: 'var(--fg-brand)',
              background: 'transparent',
            }}
          >
            {useBackupCode ? 'Use authenticator code' : 'Use a backup code'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
            style={{
              color: 'var(--fg-tertiary)',
              background: 'transparent',
            }}
          >
            ← Try a different account
          </button>
        </div>
      </form>
    </section>
  );
}
